import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Users, User, ArrowLeft, Send, CheckCircle2, UserPlus, Smartphone, QrCode,
  ChevronRight, XCircle, FileText, MessageSquare, SlidersHorizontal,
} from 'lucide-react';
import ContactBuyerPreferencesEditor from '../../contacts/components/ContactBuyerPreferencesEditor';
import OpenHomeQrModal from '../components/OpenHomeQrModal';
import SendContractModal from '../components/SendContractModal';
import BulkCommunicatorModal from '../components/BulkCommunicatorModal';
import InvitePortalModal from '../components/InvitePortalModal';
import {
  getInspection,
  getInspectionAttendees,
  closeOpenHome,
  sendPostVisitSms,
  sendContractOfSale,
  getBulkSmsPayload,
  markSmsHandedOff,
  qualifyAttendee,
  promoteAttendeeToLead,
  searchInspectionBuyers,
  postSessionCheckin,
} from '../../../api/inspection';
import { getContacts, inviteContactToPortal } from '../../../api/contact';
import QualifyAttendeeDrawer from '../components/QualifyAttendeeDrawer';
import { inspectionTerms } from '../utils/inspectionTerms';

/**
 * The live "agent device" screen the agent uses during an open home.
 *
 * Combines: agent walk-in check-in form + live attendee list (polled) + bulk
 * post-visit SMS + per-buyer qualify drawer + promote-to-lead.
 *
 * QR-based self check-ins from buyers' phones land here too because the BE
 * writes them to the same Inspection. Polling picks them up.
 */
export default function OpenHomeSessionPage() {
  const { sessionId } = useParams();
  const [inspection, setInspection] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAttendee, setActiveAttendee] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendingContract, setSendingContract] = useState(false);
  const [closing, setClosing] = useState(false);
  // The attendee whose Buying Preferences the agent is editing (opens the modal).
  const [prefsAttendee, setPrefsAttendee] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showBulkCommunicator, setShowBulkCommunicator] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);

  // "Renter" wording (and no "Contract Requested") on a lease listing. Falls
  // back to the buyer wording while the inspection is still loading.
  const terms = inspectionTerms(inspection);

  const loadAll = useCallback(async () => {
    try {
      const [insp, att] = await Promise.all([
        getInspection(sessionId),
        getInspectionAttendees(sessionId),
      ]);
      setInspection(insp.data);
      setAttendees(Array.isArray(att.data) ? att.data : att.data?.results || []);
    } catch (err) {
      console.error(err);
      toast.error('Could not load this open home.');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll attendees every 8s for QR self check-ins.
  useEffect(() => {
    const id = setInterval(() => {
      getInspectionAttendees(sessionId)
        .then((res) => setAttendees(Array.isArray(res.data) ? res.data : res.data?.results || []))
        .catch(() => {});
    }, 8000);
    return () => clearInterval(id);
  }, [sessionId]);

  const handleBulkSms = async () => {
    if (!attendees.length) { toast('No attendees to message.'); return; }
    if (!window.confirm(`Send post-visit SMS to ${attendees.length} ${terms.nounLower}${attendees.length === 1 ? '' : 's'}?`)) return;
    setSending(true);
    try {
      const res = await sendPostVisitSms(sessionId);
      toast.success(`SMS sent to ${res.data.sent?.length || 0} ${terms.nounLower}(s).`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  const handleSendContract = async (attendeeIds) => {
    if (!attendeeIds?.length) { toast(`Select at least one ${terms.nounLower}.`); return; }
    setSendingContract(true);
    try {
      const res = await sendContractOfSale(sessionId, { attendee_ids: attendeeIds });
      toast.success(`Contract of Sale sent to ${res.data.sent?.length || 0} buyer(s).`);
      setShowContractModal(false);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed.');
    } finally {
      setSendingContract(false);
    }
  };

  // Resolve an attendee's CRM contact (already linked, or found by email),
  // then send the buyer-portal sign-up link to each — same lookup PropertyDetailPage
  // uses for the single-attendee invite button.
  const handleSendPortalInvites = async (attendeeIds) => {
    if (!attendeeIds?.length) { toast(`Select at least one ${terms.nounLower}.`); return; }
    setSendingInvites(true);
    let sent = 0;
    let failed = 0;
    try {
      for (const id of attendeeIds) {
        const att = attendees.find((a) => a.id === id);
        if (!att) { failed += 1; continue; }
        try {
          let contactId = att.contact;
          if (!contactId && att.email) {
            const res = await getContacts({ search: att.email });
            const contacts = res.data?.results || res.data || [];
            contactId = contacts[0]?.id || null;
          }
          if (!contactId) { failed += 1; continue; }
          await inviteContactToPortal(contactId);
          sent += 1;
        } catch {
          failed += 1;
        }
      }
      if (sent) toast.success(`Portal invite sent to ${sent} ${terms.nounLower}${sent === 1 ? '' : 's'}.`);
      if (failed) toast.error(`Could not invite ${failed} ${terms.nounLower}${failed === 1 ? '' : 's'}.`);
      setShowInviteModal(false);
    } finally {
      setSendingInvites(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm(`Close this open home? The ${terms.nounLower} check-in link will stay live for 24h afterwards.`)) return;
    setClosing(true);
    try {
      await closeOpenHome(sessionId);
      toast.success('Open home closed.');
      loadAll();
    } catch (err) {
      toast.error('Could not close the session.');
    } finally {
      setClosing(false);
    }
  };

  const handleQualifySave = async (fields) => {
    if (!activeAttendee) return;
    try {
      const res = await qualifyAttendee(activeAttendee.id, fields);
      setAttendees((prev) => prev.map((a) => (a.id === activeAttendee.id ? res.data : a)));
      setActiveAttendee(res.data);
      toast.success('Saved.');
    } catch (err) {
      toast.error('Could not save.');
    }
  };

  // A note added (or deleted) in the drawer restamps the row behind it right
  // away. The 8s poll would get there eventually, but the agent needs to see
  // their note landed the moment they save it.
  const handleNoteChange = (attendeeId, note) => {
    setAttendees((prev) => prev.map((a) => (a.id === attendeeId ? { ...a, latest_note: note } : a)));
    setActiveAttendee((cur) => (cur && cur.id === attendeeId ? { ...cur, latest_note: note } : cur));
  };

  // Inline interest-level (Cold/Warm/Hot) set straight from the attendee row,
  // optimistically applied so the pill responds instantly.
  const handleSetInterest = async (attendee, level) => {
    if (attendee.interest_level === level) return;
    setAttendees((prev) => prev.map((a) => (a.id === attendee.id ? { ...a, interest_level: level } : a)));
    try {
      const res = await qualifyAttendee(attendee.id, { interest_level: level });
      setAttendees((prev) => prev.map((a) => (a.id === attendee.id ? res.data : a)));
    } catch (err) {
      // Roll back on failure.
      setAttendees((prev) => prev.map((a) => (a.id === attendee.id ? attendee : a)));
      toast.error('Could not update interest level.');
    }
  };

  const handlePromote = async (attendee) => {
    if (!window.confirm(`Promote ${attendee.full_name || attendee.name} to a Hot Lead?`)) return;
    try {
      const res = await promoteAttendeeToLead(attendee.id);
      toast.success('Promoted to lead.');
      loadAll();
    } catch {
      toast.error('Promotion failed.');
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  }
  if (!inspection) {
    return <div className="p-8 text-sm text-gray-500">Open home not found.</div>;
  }

  return (
    <div className="min-h-screen bg-[#F9FBFC]">
      <header className="bg-white border-b px-4 py-3 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
        <Link to="/inspections" className="flex items-center text-sm text-slate-600 hover:text-slate-900 shrink-0">
          <ArrowLeft className="w-4 h-4 mr-1" /> Inspections
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowInviteModal(true)}
            disabled={!attendees.length}
            className="flex-1 sm:flex-none justify-center text-xs flex items-center gap-1 border rounded-md px-3 py-2 sm:py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap"
            title={`Email selected ${terms.nounPluralLower} a portal sign-up link`}
          >
            <UserPlus className="w-3.5 h-3.5" /> Invite to Portal
          </button>
          <button
            onClick={() => setShowQrModal(true)}
            className="flex-1 sm:flex-none justify-center text-xs flex items-center gap-1 border rounded-md px-3 py-2 sm:py-1.5 text-slate-700 hover:bg-slate-50 whitespace-nowrap"
            title="Show signage QR"
          >
            <QrCode className="w-3.5 h-3.5" /> Show QR
          </button>
          <button
            onClick={() => setShowBulkCommunicator(true)}
            disabled={!attendees.length}
            className="flex-1 sm:flex-none justify-center text-xs flex items-center gap-1 bg-[#4EADB7] hover:bg-[#3f9ba4] disabled:opacity-50 text-white rounded-md px-3 py-2 sm:py-1.5 whitespace-nowrap"
            title={`Text ${terms.nounPluralLower} from your own number`}
          >
            <MessageSquare className="w-3.5 h-3.5" /> Bulk Communicator
          </button>
          <button
            onClick={handleBulkSms}
            disabled={sending || !attendees.length}
            className="flex-1 sm:flex-none justify-center text-xs flex items-center gap-1 border rounded-md px-3 py-2 sm:py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap"
            title="Send from the Collings business number via Twilio"
          >
            <Send className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Post-visit SMS'}
          </button>
          <button
            onClick={() => setShowContractModal(true)}
            disabled={!attendees.length}
            className="flex-1 sm:flex-none justify-center text-xs flex items-center gap-1 bg-[#4EADB7] hover:bg-[#3f9ba4] disabled:opacity-50 text-white rounded-md px-3 py-2 sm:py-1.5 whitespace-nowrap"
          >
            <FileText className="w-3.5 h-3.5" /> Send Contract of Sale
          </button>
          {inspection.status !== 'completed' && (
            <button
              onClick={handleClose}
              disabled={closing}
              className="flex-1 sm:flex-none justify-center text-xs flex items-center gap-1 border rounded-md px-3 py-2 sm:py-1.5 text-slate-700 hover:bg-slate-50 whitespace-nowrap"
            >
              <XCircle className="w-3.5 h-3.5" /> {closing ? 'Closing…' : 'Close session'}
            </button>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <section className="md:col-span-2 bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h1 className="text-base font-semibold text-slate-900">{inspection.property_title}</h1>
              <p className="text-xs text-slate-500">{inspection.property_address}</p>
            </div>
            <div className="flex items-center gap-2">
              {inspection.qr_code_url && (
                <a href={inspection.qr_code_url} target="_blank" rel="noreferrer"
                   className="text-[11px] flex items-center gap-1 text-slate-600 hover:text-slate-900">
                  <QrCode className="w-3.5 h-3.5" /> QR
                </a>
              )}
              <span className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2.5 py-0.5">
                <Users className="w-3 h-3 inline-block mr-1" /> {attendees.length} checked in
              </span>
            </div>
          </div>

          <AgentDeviceCheckinForm
            sessionId={sessionId}
            checkinToken={inspection.checkin_token}
            terms={terms}
            onCheckedIn={loadAll}
          />
        </section>

        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="text-sm font-semibold text-slate-900">Attendees</h2>
          {inspection.property_suburb && (
            <p className="text-[11px] text-slate-400 mb-3">
              Everyone who checks in is added to the registry for {inspection.property_suburb} automatically.
              Tap a {terms.nounLower}&apos;s <span className="text-slate-500 font-medium">{terms.prefsLabel}</span> to refine what they&apos;re after.
            </p>
          )}
          {attendees.length === 0 && (
            <p className="text-xs text-slate-500">No one has checked in yet.</p>
          )}
          <div className="divide-y">
            {attendees.map((a) => (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                onClick={() => setActiveAttendee(a)}
                onKeyDown={(e) => { if (e.key === 'Enter') setActiveAttendee(a); }}
                className="w-full py-2.5 text-left hover:bg-slate-50 px-1 rounded cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-800 text-[11px] font-medium flex items-center justify-center shrink-0">
                    {initials(a)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-slate-900 truncate">{a.full_name || a.name}</span>
                      {a.times_viewed > 1 && (
                        <span
                          className="shrink-0 text-[10px] font-semibold bg-[#EAF8FE] text-[#2E9BD6] rounded-full px-2 py-0.5"
                          title={`This ${terms.nounLower} has attended an open home for this property ${a.times_viewed} times`}
                        >
                          {a.times_viewed === 2 ? 'Viewed twice' : `Viewed ${a.times_viewed} times`}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      {a.checkin_method === 'qr_self_checkin'
                        ? <><QrCode className="w-3 h-3" /> Via QR</>
                        : <><Smartphone className="w-3 h-3" /> Via agent device</>}
                      {a.finance_status === 'pre_approved' && <span className="ml-1 text-emerald-700">· Finance ✓</span>}
                    </div>
                    {/* Who took these details, and when — mirrors the auto note
                        written on the buyer's contact record. Never clipped: the
                        whole point is the agent's name AND the date, and
                        "Christian Gravias entered in details · 12/8/2026" is
                        wider than this column, so it wraps rather than
                        truncating to "...entered in d..". */}
                    {a.entered_by_name ? (
                      <div className="text-[11px] text-slate-500 leading-snug break-words">
                        <span className="font-medium">{a.entered_by_name}</span> entered in details
                        {a.check_in_time ? ` · ${shortDate(a.check_in_time)}` : ''}
                      </div>
                    ) : a.checkin_method !== 'qr_self_checkin' ? (
                      /* Someone keyed this buyer in on an agent device but we
                         have no name for them — either the buyer was added
                         before we started recording it, or the agent's session
                         had expired. Say so, otherwise a blank line reads as
                         the feature being broken. */
                      <div
                        className="text-[11px] text-slate-400 leading-snug"
                        title="This buyer was added before the CRM started recording who entered the details, or the agent's session had expired at the time."
                      >
                        Agent who entered details not recorded
                      </div>
                    ) : null}
                    {/* The newest note on this buyer, stamped with who wrote it
                        and when — the same line, from the same thread, that the
                        property's Attendees tab shows. Tap the row to read the
                        rest or add another. */}
                    {a.latest_note?.text && (
                      <div className="mt-1 rounded-md border border-amber-100 bg-amber-50 px-2 py-1.5">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-amber-600">Last note</div>
                        <p className="text-[11px] leading-snug text-amber-900 whitespace-pre-wrap break-words">
                          {a.latest_note.text}
                        </p>
                        <div className="text-[10px] font-medium text-amber-700/80">
                          {[a.latest_note.author, a.latest_note.created_at ? shortDate(a.latest_note.created_at) : null]
                            .filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    )}
                    {((terms.showContractRequested && a.contract_requested) || a.property_to_sell) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {terms.showContractRequested && a.contract_requested && (
                          <span className="text-[10px] font-semibold bg-[#EAF8FE] text-[#2E9BD6] rounded-full px-2 py-0.5">
                            Contract requested
                          </span>
                        )}
                        {a.property_to_sell && (
                          <span className="text-[10px] font-semibold bg-[#FFF4E8] text-[#F2994A] rounded-full px-2 py-0.5">
                            Has property to sell
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {a.contact && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setPrefsAttendee(a); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setPrefsAttendee(a); } }}
                      className="text-[11px] flex items-center gap-1 text-slate-500 hover:text-[#4EADB7] shrink-0"
                      title={`Edit this ${terms.nounLower}'s ${terms.prefsLabel}`}
                    >
                      <SlidersHorizontal className="w-3.5 h-3.5" />
                    </span>
                  )}
                  {a.converted_to_lead
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    : (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); handlePromote(a); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handlePromote(a); } }}
                        className="text-[11px] flex items-center gap-1 text-slate-500 hover:text-slate-900 shrink-0"
                        title="Promote to Hot Lead"
                      >
                        <UserPlus className="w-3 h-3" />
                      </span>
                    )}
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                </div>
                <InterestPills
                  value={a.interest_level}
                  onChange={(level) => handleSetInterest(a, level)}
                />
              </div>
            ))}
          </div>
        </section>
      </div>

      {activeAttendee && (
        <QualifyAttendeeDrawer
          attendee={activeAttendee}
          terms={terms}
          onClose={() => setActiveAttendee(null)}
          onSave={handleQualifySave}
          onNoteChange={(note) => handleNoteChange(activeAttendee.id, note)}
        />
      )}

      {prefsAttendee && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
          onClick={() => setPrefsAttendee(null)}
        >
          <div
            className="bg-[#F9FBFC] rounded-2xl shadow-xl w-full max-w-3xl my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b bg-white rounded-t-2xl">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{terms.prefsLabel}</h3>
                <p className="text-[11px] text-slate-500">{prefsAttendee.full_name || prefsAttendee.name}</p>
              </div>
              <button
                onClick={() => setPrefsAttendee(null)}
                className="text-slate-400 hover:text-slate-700"
                title="Close"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <ContactBuyerPreferencesEditor
              contactId={prefsAttendee.contact}
              onSaved={() => { setPrefsAttendee(null); loadAll(); }}
            />
          </div>
        </div>
      )}

      {showQrModal && (
        <OpenHomeQrModal
          inspection={inspection}
          onClose={() => setShowQrModal(false)}
        />
      )}

      {showContractModal && (
        <SendContractModal
          attendees={attendees}
          sending={sendingContract}
          onClose={() => setShowContractModal(false)}
          onSend={handleSendContract}
        />
      )}

      {showInviteModal && (
        <InvitePortalModal
          attendees={attendees}
          sending={sendingInvites}
          onClose={() => setShowInviteModal(false)}
          onSend={handleSendPortalInvites}
        />
      )}

      {showBulkCommunicator && (
        <BulkCommunicatorModal
          attendees={attendees}
          loadMessages={async ({ attendeeIds, template }) => {
            const res = await getBulkSmsPayload(sessionId, { attendee_ids: attendeeIds, template });
            return { messages: res.data?.messages || [], skipped: res.data?.skipped || [] };
          }}
          markHandedOff={async (ids) => { await markSmsHandedOff(sessionId, ids); }}
          onClose={() => { setShowBulkCommunicator(false); loadAll(); }}
        />
      )}
    </div>
  );
}

const TEMPERATURES = [
  { key: 'NONE', label: 'No Interest', active: 'bg-gray-500 text-white shadow-lg shadow-gray-500/30', idle: 'bg-gray-100 text-gray-500' },
  { key: 'COLD', label: 'COLD', active: 'bg-[#56CCF2] text-white shadow-lg shadow-[#56CCF2]/30', idle: 'bg-[#EAF8FE] text-[#56CCF2]' },
  { key: 'WARM', label: 'WARM', active: 'bg-[#F2994A] text-white shadow-lg shadow-[#F2994A]/30', idle: 'bg-[#FFF4E8] text-[#F2994A]' },
  { key: 'HOT',  label: 'HOT',  active: 'bg-[#3ACA6F] text-white shadow-lg shadow-[#3ACA6F]/30', idle: 'bg-[#EBF9F0] text-[#3ACA6F]' },
];

const INTEREST_MAP = { NONE: 'none', COLD: 'cold', WARM: 'warm', HOT: 'hot' };

// Inline Cold / Warm / Hot selector shown on each attendee row. Click sets the
// level immediately (handled by the parent); stopPropagation keeps the row's
// own click (open qualify drawer) from firing.
const INTEREST_PILLS = [
  { level: 'none', label: 'No Interest', active: 'bg-gray-500 text-white', idle: 'bg-gray-100 text-gray-500 hover:bg-gray-200' },
  { level: 'cold', label: 'Cold', active: 'bg-[#56CCF2] text-white', idle: 'bg-[#EAF8FE] text-[#56CCF2] hover:bg-[#d8f1fc]' },
  { level: 'warm', label: 'Warm', active: 'bg-[#F2994A] text-white', idle: 'bg-[#FFF4E8] text-[#F2994A] hover:bg-[#ffe9d2]' },
  { level: 'hot',  label: 'Hot',  active: 'bg-[#3ACA6F] text-white', idle: 'bg-[#EBF9F0] text-[#3ACA6F] hover:bg-[#d8f3e1]' },
];

function InterestPills({ value, onChange }) {
  return (
    <div className="flex gap-1 mt-1.5 pl-11" onClick={(e) => e.stopPropagation()}>
      {INTEREST_PILLS.map(({ level, label, active, idle }) => (
        <button
          key={level}
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(level); }}
          className={`flex-1 py-1 rounded-md text-[11px] font-semibold transition-all ${value === level ? active : idle}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function AgentDeviceCheckinForm({ sessionId, checkinToken, terms = inspectionTerms(null), onCheckedIn }) {
  const initial = { name: '', phone: '', email: '', notes: '',
                    contract_requested: false, has_property_to_sell: false,
                    temperature: 'WARM' };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  // Typeahead — re-add a known contact without re-keying their details.
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [picked, setPicked] = useState(false); // a suggestion was just chosen
  const searchTimer = useRef(null);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  // Debounced search on name, phone OR email — buyers sometimes give a number
  // or email first (CRM-2026-00039 revision). The backend matches all three.
  const runSearch = (q) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = (q || '').trim();
    if (term.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await searchInspectionBuyers(sessionId, term);
        setSuggestions(Array.isArray(res.data) ? res.data : []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 250);
  };

  const onFieldChange = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    setPicked(false);
    setActiveField(field);
    runSearch(value);
  };

  const pickSuggestion = (s) => {
    setForm((f) => ({
      ...f,
      name: s.name || `${s.first_name} ${s.last_name}`.trim(),
      phone: s.phone || '',
      email: s.email || '',
    }));
    setShowSuggestions(false);
    setSuggestions([]);
    setPicked(true);
  };

  // Shared dropdown, rendered under whichever field is active.
  const renderSuggestions = (field) => (
    showSuggestions && activeField === field && suggestions.length > 0 && (
      <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-64 overflow-auto">
        {suggestions.map((s) => (
          <li key={s.contact_id}>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2"
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900 truncate">{s.name}</span>
                <span className="block text-[11px] text-slate-500 truncate">
                  {[s.phone, s.email].filter(Boolean).join(' · ') || 'No contact details'}
                </span>
              </span>
              {s.times_viewed > 0 && (
                <span className="shrink-0 text-[10px] font-semibold bg-[#EAF8FE] text-[#2E9BD6] rounded-full px-2 py-0.5">
                  {s.times_viewed === 1 ? 'Viewed once'
                    : s.times_viewed === 2 ? 'Viewed twice'
                    : `Viewed ${s.times_viewed} times`}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    )
  );

  const submit = async (e) => {
    e.preventDefault();
    const fullName = (form.name || '').trim();
    if (!fullName) {
      toast.error(`Please enter the ${terms.nounLower}'s full name.`);
      return;
    }
    setBusy(true);
    try {
      const nameParts = fullName.split(/\s+/);
      const phone = (form.phone || '').trim();
      const email = (form.email || '').trim();
      const payload = {
        name: fullName,
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' '),
        phone,
        mobile: phone,
        email: email || null,
        checkin_method: 'agent_device',
        interest_level: INTEREST_MAP[form.temperature] || 'warm',
        // Structured so they flow through to the attendee record / qualify drawer
        // rather than being buried as text in the notes.
        property_to_sell: form.has_property_to_sell || null,
        contract_requested: (terms.showContractRequested && form.contract_requested) || null,
        additional_notes: (form.notes || '').trim(),
      };
      // Hit the public token-based check-in so the agent device and the QR
      // page share one code path on the backend. Sent with the agent's
      // credentials so the buyer's contact gets the "<Agent> entered in
      // details - <date>" auto note.
      const res = await postSessionCheckin(checkinToken, payload);
      if (!res.ok) throw new Error('Check-in failed');
      toast.success(
        email
          ? `Added ${fullName}. A confirmation email has been sent.`
          : `Added ${fullName}.`
      );
      setForm(initial);
      setSuggestions([]);
      setShowSuggestions(false);
      setPicked(false);
      onCheckedIn?.();
    } catch (err) {
      toast.error(err.message || `Could not add ${terms.nounLower}.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6 text-left font-sans">
      {/* Buyer / Renter Details Section */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-[#353B48]" />
          <span className="text-sm font-bold text-[#353B48]">{terms.noun} Details</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <label className="block space-y-1">
              <span className="text-xs font-bold text-[#353B48]">Full Name *</span>
              <input
                value={form.name}
                onChange={onFieldChange('name')}
                onFocus={() => { if (activeField === 'name' && suggestions.length) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoComplete="off"
                placeholder={`Start typing to find an existing ${terms.nounLower}…`}
                className="w-full px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] text-sm"
              />
            </label>
            {picked && (
              <span className="absolute right-3 top-[34px] text-[10px] text-emerald-600 font-semibold">
                ✓ details filled
              </span>
            )}
            {renderSuggestions('name')}
          </div>
          <div className="relative">
            <label className="block space-y-1">
              <span className="text-xs font-bold text-[#353B48]">Phone</span>
              <input
                type="tel"
                value={form.phone}
                onChange={onFieldChange('phone')}
                onFocus={() => { if (activeField === 'phone' && suggestions.length) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoComplete="off"
                placeholder={`Type a phone to find an existing ${terms.nounLower}…`}
                className="w-full px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] text-sm"
              />
            </label>
            {renderSuggestions('phone')}
          </div>
          <div className="relative">
            <label className="block space-y-1">
              <span className="text-xs font-bold text-[#353B48]">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={onFieldChange('email')}
                onFocus={() => { if (activeField === 'email' && suggestions.length) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoComplete="off"
                placeholder="Type an email to find them, or leave for portal invite"
                className="w-full px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] text-sm"
              />
            </label>
            {renderSuggestions('email')}
          </div>
        </div>

        <div className="mt-4 space-y-1">
          <label className="text-xs font-bold text-[#353B48]">Notes</label>
          <textarea
            rows="3"
            className="w-full px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] text-sm resize-none"
            placeholder="Any additional notes about this contact..."
            value={form.notes}
            onChange={set('notes')}
          />
        </div>
      </div>

      {/* Buyer / Renter Info Section */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-[#353B48]" />
          <span className="text-sm font-bold text-[#353B48]">{terms.noun} Info</span>
        </div>

        <div className="flex gap-6 mb-4">
          {terms.showContractRequested && (
            <Check label="Contract Requested" checked={form.contract_requested} onChange={set('contract_requested')} />
          )}
          <Check label="Has Property to sell" checked={form.has_property_to_sell} onChange={set('has_property_to_sell')} />
        </div>

        <div className="grid grid-cols-4 gap-3">
          {TEMPERATURES.map(({ key, label, active, idle }) => (
            <button
              key={key}
              type="button"
              onClick={() => setForm((f) => ({ ...f, temperature: key }))}
              className={`py-2 rounded-lg font-bold text-xs transition-all ${form.temperature === key ? active : idle}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <button type="submit" disabled={busy}
        className="w-full bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg py-2.5 transition-all shadow-md shadow-[#55C5D0]/20">
        {busy ? 'Adding…' : terms.addLabel}
      </button>
    </form>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 border-[#E5E7EB] rounded text-[#55C5D0] focus:ring-[#55C5D0]"
      />
      <span className="text-xs font-medium text-[#353B48]">{label}</span>
    </label>
  );
}

function initials(a) {
  const first = (a.first_name || a.name || '?')[0] || '?';
  const last = (a.last_name || (a.name || '').split(' ')[1] || '')[0] || '';
  return `${first}${last}`.toUpperCase();
}

// d/m/yyyy, the way the agency writes dates.
function shortDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}
